/**
 * UserStrategySidebar — sidebar listing all user strategies with search.
 * Strategy creation is handled exclusively via the connected agent (MCP).
 */

import { useRef, useState } from 'react';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { useUserStrategies } from '../../hooks/useUserStrategies.js';
import { useScrollToSelected } from '../../hooks/useScrollToSelected.js';
import { usePinnedStrategiesStore } from '../../stores/pinnedStrategiesStore.js';
import { UserStrategyCard } from './UserStrategyCard.js';
import { RenameStrategyModal } from './RenameStrategyModal.js';
import { DeleteStrategyModal } from './DeleteStrategyModal.js';
import { Spinner } from '../Spinner/Spinner.js';
import type { UserStrategy } from '../../types.js';

// SHA-43 item 5: widened from 320 -> 360 so longer strategy names truncate less
// aggressively; combined with the title-attr tooltip added to UserStrategyCard.
const SIDEBAR_WIDTH = 360;

export function UserStrategySidebar() {
  const {
    searchQuery,
    setSearchQuery,
    selectedStrategyId,
    setSelectedStrategyId,
  } = useConfigurationStore();

  const listRef = useRef<HTMLDivElement>(null);
  useScrollToSelected(selectedStrategyId, listRef);

  const { data: strategies, isLoading, error } = useUserStrategies();
  const { pinned, isPinned, toggle: togglePin } = usePinnedStrategiesStore();

  // SHA-43 item 1/2: rename/delete modal target
  const [renameTarget, setRenameTarget] = useState<UserStrategy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserStrategy | null>(null);

  const searched = strategies?.filter((s) => {
    const q = searchQuery.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
  });

  // SHA-43 item 3: pinned strategies float to the top, most-recently-pinned first.
  // Non-pinned strategies keep their original (server) order.
  const filtered = searched
    ? [...searched].sort((a, b) => {
        const aPinIdx = pinned.indexOf(a.id);
        const bPinIdx = pinned.indexOf(b.id);
        const aPinned = aPinIdx !== -1;
        const bPinned = bPinIdx !== -1;
        if (aPinned && bPinned) return aPinIdx - bPinIdx;
        if (aPinned) return -1;
        if (bPinned) return 1;
        return 0;
      })
    : searched;

  return (
    <aside
      style={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        flexShrink: 0,
        borderRight: '1px solid #333',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Search */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a2a', flexShrink: 0 }}>
        <input
          type="text"
          placeholder="Search strategies, symbols..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 10px',
            background: '#252525',
            border: '1px solid #444',
            borderRadius: 6,
            color: '#e0e0e0',
            fontSize: 13,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* List */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner size="lg" className="text-gray-400" />
          </div>
        )}

        {error && (
          <div
            style={{
              margin: 12,
              padding: '10px 12px',
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid #7f1d1d',
              borderRadius: 6,
              color: '#fca5a5',
              fontSize: 13,
            }}
          >
            Failed to load strategies
          </div>
        )}

        {!isLoading && filtered && filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#555', fontSize: 13 }}>
            {searchQuery ? 'No strategies match your search.' : 'No strategies yet. Strategies are created via the connected agent (MCP).'}
          </div>
        )}

        {filtered && filtered.length > 0 && filtered.map((strategy) => (
          <div key={strategy.id} data-item-id={strategy.id}>
            <UserStrategyCard
              strategy={strategy}
              isSelected={strategy.id === selectedStrategyId}
              onClick={() =>
                setSelectedStrategyId(strategy.id === selectedStrategyId ? null : strategy.id)
              }
              isPinned={isPinned(strategy.id)}
              onTogglePin={() => togglePin(strategy.id)}
              onRename={() => setRenameTarget(strategy)}
              onDelete={() => setDeleteTarget(strategy)}
            />
          </div>
        ))}
      </div>

      {/* Footer stats */}
      {filtered && filtered.length > 0 && (
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid #2a2a2a',
            fontSize: 12,
            color: '#666',
          }}
        >
          {filtered.length} strateg{filtered.length !== 1 ? 'ies' : 'y'}
          {searchQuery && strategies && ` (${strategies.length} total)`}
        </div>
      )}

      {renameTarget && (
        <RenameStrategyModal
          isOpen={true}
          strategyId={renameTarget.id}
          currentName={renameTarget.name}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteStrategyModal
          isOpen={true}
          strategyId={deleteTarget.id}
          strategyName={deleteTarget.name}
          configCount={deleteTarget.configCount ?? 0}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            if (selectedStrategyId === deleteTarget.id) setSelectedStrategyId(null);
          }}
        />
      )}
    </aside>
  );
}
